// eslint-disable-next-line
import React, { ComponentType, FC, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import Grid from '@material-ui/core/Grid';
import Modal from 'components/Modal';
import WizardLinkButton from 'components/LinkButton/WizardLinkButton.styles';
import OpacityAnimation from 'components/OpacityAnimation';
import Paper from '@material-ui/core/Paper';
import { useCallbackPrompt } from 'utils/hooks';
import Box from '@material-ui/core/Box';
import { selectOperationId, setOperationId } from 'store/modules/config';
import { useAppDispatch } from 'store';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { TextTwo } from 'components/typography';
import Divider from '@material-ui/core/Divider';
import { WizardFab, WizardGridMenu } from './Wizard.styles';
import { ModalResponseReason, useWizard } from './context';
import { selectFlow } from 'store/modules/main';

export type WizardProps = {
  children: any;
  topMenu?: ComponentType<{}>;
};

const Wizard: FC<WizardProps> = ({ children, topMenu: TopMenu }) => {
  const [wizardState, wizardDispatch] = useWizard();
  const { currentModal } = wizardState;
  const stepsCount = React.Children.count(children) - 1;
  const isCurrentStep = (index: number) => index === wizardState.currentStep;
  const disableStep = (index: number) => index > wizardState.currentStep;
  const stepVisited = (index: number) => wizardState.currentStep > index;
  const { setStepsCount, openModal } = wizardDispatch;
  const dispatch = useAppDispatch();
  const operationId = useSelector(selectOperationId);
  const flow = useSelector(selectFlow);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    !wizardState.flowCompleted
  );

  useEffect(() => {
    if (
      !currentModal.show &&
      showPrompt &&
      !wizardState.flowCompleted &&
      !!flow
    ) {
      openModal('EXIT_PROCESS');
    }
  }, [
    currentModal.show,
    openModal,
    showPrompt,
    wizardState.flowCompleted,
    flow,
  ]);

  const modalResponse = (reason: ModalResponseReason) => {
    switch (currentModal.type) {
      case 'EXIT_PROCESS': {
        if (reason === ModalResponseReason.CONFIRM) {
          confirmNavigation();
          dispatch(setOperationId(''));
        } else {
          cancelNavigation();
        }
        wizardDispatch.closeModal();
        break;
      }
      case 'CHANGE_STEP': {
        if (reason === ModalResponseReason.CONFIRM) {
          wizardDispatch.goToStep(currentModal.nextStep);
        }
        wizardDispatch.closeModal();
        break;
      }
    }
  };

  const changeStep = (nextStep) => {
    if (isCurrentStep(nextStep) || wizardState.flowCompleted) return;
    openModal('CHANGE_STEP', nextStep);
  };

  useEffect(() => {
    setStepsCount(stepsCount);
  }, [stepsCount, setStepsCount]);

  useEffect(() => {
    dispatch(setOperationId(uuid()));
  }, [dispatch]);

  const renderNavigation = React.Children.map(children, ({ props }, index) => {
    return (
      <Box p={3}>
        <WizardFab
          size="small"
          color="primary"
          aria-label={props.title}
          disableRipple
          disableFocusRipple
          $withLine={stepsCount !== index}
          $stepVisited={stepVisited(index)}
          disabled={disableStep(index)}
          onClick={() => changeStep(index)}
          data-testid="wizard-step-indicator"
        >
          {index + 1}
        </WizardFab>
        <WizardLinkButton
          disabled={disableStep(index)}
          color={isCurrentStep(index) ? 'primary' : 'default'}
          $stepVisited={stepVisited(index)}
          onClick={() => changeStep(index)}
          data-testid="wizard-menu-step-name"
        >
          {props.title}
        </WizardLinkButton>
      </Box>
    );
  });

  const renderSteps = React.Children.map(children, (stepComponent, index) => {
    return React.cloneElement(stepComponent, {
      step: index,
      stepState: wizardState.stepsState[index],
      stepsState: wizardState.stepsState,
      isActive: isCurrentStep(index),
      key: index,
    });
  });

  return (
    <OpacityAnimation>
      <Paper>
        <Grid container>
          <WizardGridMenu item container direction="column" sm={3} lg={2}>
            {TopMenu && <TopMenu />}
            {renderNavigation}
            <Box p={3} mt="auto">
              <Divider light />
              <Box pt={2} textAlign="center">
                <TextTwo>ID N° {operationId}</TextTwo>
              </Box>
            </Box>
          </WizardGridMenu>
          <Grid item xs>
            <Box px={3} py={2}>
              <AnimatePresence>{renderSteps}</AnimatePresence>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Modal
        open={currentModal.show}
        title={currentModal.title}
        onOk={() => modalResponse(ModalResponseReason.CONFIRM)}
        onCancel={() => modalResponse(ModalResponseReason.CANCEL)}
      >
        {currentModal.content}
      </Modal>
    </OpacityAnimation>
  );
};

export default Wizard;
